import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { LineItemType } from '../../config';
import './invoice-slash-menu.scss';

interface SlashOption {
  type: LineItemType;
  label: string;
  icon: string;
  category: 'line' | 'text' | 'layout';
}

interface InvoiceSlashMenuProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (type: LineItemType) => void;
  onChangeType: (type: LineItemType) => void;
  onDelete: () => void;
  position: { top: number; left: number };
  searchQuery: string;
  currentType: LineItemType;
}

const LINE_OPTIONS: SlashOption[] = [
  { type: 'item', label: 'Fee Item', icon: '$', category: 'line' },
  { type: 'disbursement', label: 'Disbursement', icon: 'D', category: 'line' },
  { type: 'deduction', label: 'Deduction', icon: '–', category: 'line' },
  { type: 'header', label: 'Header', icon: 'H', category: 'text' },
  { type: 'text', label: 'Text', icon: 'T', category: 'text' },
  { type: 'bullet', label: 'Bullet Point', icon: '-', category: 'text' },
  { type: 'drawing', label: 'Drawing', icon: '#', category: 'text' },
  { type: 'spacer', label: 'Spacer', icon: '↕', category: 'layout' },
];

const CATEGORY_LABELS: Record<string, string> = {
  line: 'Line Items',
  text: 'Text',
  layout: 'Layout',
};

const InvoiceSlashMenu: React.FC<InvoiceSlashMenuProps> = ({
  isOpen,
  onClose,
  onSelect,
  onChangeType,
  onDelete,
  position,
  searchQuery,
  currentType,
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [mode, setMode] = useState<'add' | 'change'>('add');
  const menuRef = useRef<HTMLDivElement>(null);

  const filteredOptions = useMemo(() => {
    const query = searchQuery.toLowerCase();
    return LINE_OPTIONS.filter(option => (
      option.label.toLowerCase().includes(query) ||
      option.type.toLowerCase().includes(query)
    ));
  }, [searchQuery]);

  const allItems = useMemo(() => (
    mode === 'change'
      ? [
          ...filteredOptions.filter(o => o.type !== currentType),
          ...(searchQuery === '' ? [{ type: 'delete' as LineItemType, label: 'Delete', icon: '×', category: 'action' as const }] : []),
        ]
      : filteredOptions
  ), [mode, filteredOptions, currentType, searchQuery]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [searchQuery, mode]);

  useEffect(() => {
    if (isOpen) {
      setMode('add');
    }
  }, [isOpen]);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (!isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => prev < allItems.length - 1 ? prev + 1 : prev);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : prev);
        break;
      case 'Tab':
        e.preventDefault();
        setMode(prev => prev === 'add' ? 'change' : 'add');
        break;
      case 'Enter':
        e.preventDefault();
        if (allItems[selectedIndex]) {
          const option = allItems[selectedIndex];
          if ((option as { type: string }).type === 'delete') {
            onDelete();
          } else if (mode === 'change') {
            onChangeType(option.type as LineItemType);
          } else {
            onSelect(option.type as LineItemType);
          }
        }
        break;
      case 'Escape':
        e.preventDefault();
        onClose();
        break;
    }
  }, [isOpen, allItems, selectedIndex, onSelect, onChangeType, onDelete, onClose, mode]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  useEffect(() => {
    const el = menuRef.current?.querySelector('.slash-option.selected');
    el?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  if (!isOpen) return null;

  const grouped = allItems.reduce((acc, option, index) => {
    const cat = (option as { category: string }).category || 'action';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push({ ...option, originalIndex: index });
    return acc;
  }, {} as Record<string, (typeof allItems[number] & { originalIndex: number })[]>);

  return (
    <div
      ref={menuRef}
      className="invoice-slash-menu"
      style={{ top: position.top, left: position.left }}
    >
      <div className="slash-menu__tabs">
        <button
          className={`slash-menu__tab ${mode === 'add' ? 'active' : ''}`}
          onMouseDown={(e) => { e.preventDefault(); setMode('add'); }}
        >
          Add
        </button>
        <button
          className={`slash-menu__tab ${mode === 'change' ? 'active' : ''}`}
          onMouseDown={(e) => { e.preventDefault(); setMode('change'); }}
        >
          Change
        </button>
      </div>

      {allItems.length === 0 ? (
        <div className="slash-menu__empty">No matches</div>
      ) : (
        <div className="slash-menu__options">
          {Object.entries(grouped).map(([category, options]) => (
            <div key={category} className="slash-menu__group">
              {CATEGORY_LABELS[category] && (
                <div className="slash-menu__group-label">{CATEGORY_LABELS[category]}</div>
              )}
              {options.map((option) => (
                <div
                  key={`${option.type}-${option.label}`}
                  className={`slash-option ${option.originalIndex === selectedIndex ? 'selected' : ''}`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    if ((option as { type: string }).type === 'delete') {
                      onDelete();
                    } else if (mode === 'change') {
                      onChangeType(option.type as LineItemType);
                    } else {
                      onSelect(option.type as LineItemType);
                    }
                  }}
                  onMouseEnter={() => setSelectedIndex(option.originalIndex)}
                >
                  <span className="slash-option__icon">{option.icon}</span>
                  <span className="slash-option__label">{option.label}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      <div className="slash-menu__footer">
        <span><kbd>↑</kbd><kbd>↓</kbd> navigate</span>
        <span><kbd>↵</kbd> select</span>
        <span><kbd>Tab</kbd> switch</span>
        <span><kbd>esc</kbd> close</span>
      </div>
    </div>
  );
};

export default InvoiceSlashMenu;
